import { Component, OnInit, Inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material';
import { MediaFolder, Error, RenameFolderDialogData, FolderNode, ConfirmDialogData, MediaTranslation } from 'src/app/utils/common-classes';
import { FolderService } from 'src/app/services/Folder/folder.service';
import { UtilsService } from 'src/app/services/Utils/utils.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-rename-folder-dialog',
  templateUrl: './rename-folder-dialog.component.html',
  styleUrls: ['./rename-folder-dialog.component.scss']
})
export class RenameFolderDialogComponent implements OnInit {
  public folder: FolderNode;
  public form: FormGroup;
  public renameFolderError: string;
  public formSubmitAttempt: boolean;
  public selectedLanguage: string;
  private selectedLanguageName: string;
  private selectedLanguageDescription: string;
  public creatingNew: boolean;

  constructor(
    public dialogRef: MatDialogRef<RenameFolderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RenameFolderDialogData,
    private fb: FormBuilder,
    private folderService: FolderService,
    public utils: UtilsService,
    private dialog: MatDialog) { }

  ngOnInit() {
    this.form = this.fb.group({
      folderName: ['', [Validators.required, Validators.pattern('^[^\\/?%*:|"<>\.]+$')]],
      description: ['']
    });
    this.folder = this.data.node;
    this.form.get('folderName').setValue(this.folder.name);
    this.form.get('description').setValue(this.folder.description);
    this.selectedLanguageName = this.form.get('folderName').value;
    this.selectedLanguageDescription = this.form.get('description').value;
    this.utils.setDialogUnclosable(true);
  }

  public async closeDialog() {
    let _self = this;
    if (!_self.edited() && !_self.creatingNew) {
      _self.utils.setDialogUnclosable(false);
      _self.dialogRef.close(_self.folder);
    } else {
      let title: string = await _self.utils.translateKey("dialog.confirm.title.unsaved-changes");
      let message: string = await _self.utils.translateKey("dialog.confirm.message.unsaved-changes");
      let data: ConfirmDialogData = { title: title, message: message };
      let confirmLeaveDialog: MatDialogRef<ConfirmDialogComponent> = _self.dialog.open(ConfirmDialogComponent, { width: "400px", data: data, disableClose: true });
      confirmLeaveDialog.afterClosed().subscribe((confirm: boolean) => {
        if (confirm) {
          _self.utils.setDialogUnclosable(false);
          _self.dialogRef.close(_self.folder);
        }
      });
    }
  }

  public submit(): void {
    let _self = this;
    _self.formSubmitAttempt = true;
    if (_self.form.valid) {
      let folderName = _self.form.get('folderName').value;
      let description = _self.form.get('description').value;
      _self.utils.showShortLoader();
      let mediaTranslation: MediaTranslation = new MediaTranslation();
      mediaTranslation.languageId = _self.selectedLanguage;
      mediaTranslation.name = folderName;
      mediaTranslation.description = description;
      mediaTranslation.mediaFolderId = _self.folder.mediaFolderId;
      _self.folderService.setFolderTranslation(mediaTranslation).then((folder: MediaFolder) => {
        _self.folder = FolderNode.fromMediaFolder(folder, _self.folder.level);
        _self.updateData(_self.selectedLanguage);
        _self.creatingNew = false;
      }).catch((err) => {
        return _self.utils.getError(err);
      }).finally(() => {
        _self.utils.hideShortLoader();
      });
    }
  }

  public async deleteTranslation() {
    let _self = this;
    if (!_self.creatingNew && _self.folder.translations && _self.folder.translations.get(_self.selectedLanguage)) {
      let title: string = await _self.utils.translateKey("dialog.confirm.title.delete-translation");
      let message: string = await _self.utils.translateKey("dialog.confirm.message.delete-translation");
      let data: ConfirmDialogData = { title: title, message: message };
      let confirmLeaveDialog: MatDialogRef<ConfirmDialogComponent> = _self.dialog.open(ConfirmDialogComponent, { width: "400px", data: data, disableClose: true });
      confirmLeaveDialog.afterClosed().subscribe((confirm: boolean) => {
        if (confirm) {
          let mediaTranslation: MediaTranslation = new MediaTranslation();
          mediaTranslation.mediaTranslationId = _self.folder.translations.get(_self.selectedLanguage).mediaTranslationId;
          _self.folderService.deleteTranslation(mediaTranslation).then((folder: MediaFolder) => {
            _self.folder = FolderNode.fromMediaFolder(folder, _self.folder.level);
            _self.updateData(_self.selectedLanguage);
            _self.creatingNew = false;
          }).catch((err) => {
            return _self.utils.getError(err);
          }).finally(() => {
            _self.utils.hideShortLoader();
          });
        }
      });
    } else {
      _self.updateData(_self.selectedLanguage);
      _self.creatingNew = false;
    }
  }

  public cancel(): void {
    let _self = this;
    _self.updateData(_self.selectedLanguage);
  }

  public async selectLanguage(language: string) {
    let _self = this;
    if (language != _self.selectedLanguage) {
      if (!_self.edited() && !_self.creatingNew) {
        _self.updateData(language);
      } else {
        let title: string = await _self.utils.translateKey("dialog.confirm.title.unsaved-changes");
        let message: string = await _self.utils.translateKey("dialog.confirm.message.unsaved-changes");
        let data: ConfirmDialogData = { title: title, message: message };
        let confirmLeaveDialog: MatDialogRef<ConfirmDialogComponent> = _self.dialog.open(ConfirmDialogComponent, { width: "400px", data: data, disableClose: true });
        confirmLeaveDialog.afterClosed().subscribe((cancel: boolean) => {
          if (cancel) {
            _self.updateData(language);
            _self.creatingNew = false;
          }
        });
      }
    }
  }

  private updateData(language: string): void {
    let _self = this;
    let folder: FolderNode = _self.folder;
    _self.selectedLanguageName = "";
    _self.selectedLanguageDescription = "";
    if (language) {
      if (folder.translations.get(language)) {
        _self.selectedLanguageName = _self.folder.translations.get(language).name || "";
        _self.selectedLanguageDescription = _self.folder.translations.get(language).description || "";
      }
    } else {
      _self.selectedLanguageName = _self.folder.name;
      _self.selectedLanguageDescription = _self.folder.description || "";
    }

    _self.form.get('folderName').setValue(_self.selectedLanguageName);
    _self.form.get('description').setValue(_self.selectedLanguageDescription);
    _self.selectedLanguage = language;
  }

  public edited(): boolean {
    let _self = this;
    return _self.selectedLanguageName != _self.form.get('folderName').value || _self.selectedLanguageDescription != _self.form.get('description').value;
  }

  public createNewTranslation(): void {
    let _self = this;
    _self.creatingNew = true;
  }

}
